import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Calculator,
  Search,
  Share2,
  ShieldCheck,
  Sparkles,
  Wallet,
} from 'lucide-react';
import {
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share';
import {
  NEW_VS_OLD_COUNTY_SEEDS,
  toCountyComparisonSlug,
  toTitleCase,
  toTownComparisonSlug,
} from '../../data/newBuildVsOldBuild2026';

const NewVsOldDiscoverPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCounty, setSelectedCounty] = useState('dublin');

  const selectedSeed =
    NEW_VS_OLD_COUNTY_SEEDS.find((item) => item.county === selectedCounty) ||
    NEW_VS_OLD_COUNTY_SEEDS[0];

  const selectedPath = `/new-vs-old/${toTownComparisonSlug(selectedSeed.town)}`;

  const filtered = useMemo(() => {
    const normalized = searchTerm.toLowerCase().trim();
    if (!normalized) return NEW_VS_OLD_COUNTY_SEEDS;
    return NEW_VS_OLD_COUNTY_SEEDS.filter(
      (item) => item.county.includes(normalized) || item.town.includes(normalized)
    );
  }, [searchTerm]);

  const canonicalUrl = 'https://rentsmart.ie/new-vs-old';
  const shareUrl = typeof window !== 'undefined' ? window.location.href : canonicalUrl;
  const shareText =
    'New build vs old build in Ireland: compare grants, BER, mortgage rates and 5-year costs on RentSmart.ie.';

  return (
    <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-x-hidden bg-slate-50">
      <Helmet>
        <title>New Build vs Old Build Ireland (2026 Cost Matrix)</title>
        <meta
          name="description"
          content="Compare buying a new house vs period property across Ireland. See county-level 5-year cost, BER heating gap, grants, and green mortgage differences."
        />
        <meta
          name="keywords"
          content="new build vs old build ireland, green mortgage ireland, help to buy vs seai grants, a rated vs d rated home costs"
        />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'New Build vs Old Build Ireland',
            description:
              'Decision-phase property comparison pages with county-level five-year cost and energy-efficiency analysis.',
            url: canonicalUrl,
          })}
        </script>
      </Helmet>

      <header className="bg-slate-900 text-white pt-14 pb-20 px-4 relative overflow-hidden">
        <div className="absolute -top-12 -right-8 h-80 w-80 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="absolute -bottom-20 -left-8 h-72 w-72 rounded-full bg-sky-500/20 blur-3xl" />

        <div className="relative max-w-7xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-widest mb-5">
            <ShieldCheck className="h-4 w-4" /> Compare Costs
          </div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4">
            New Build vs Old Build in Ireland: County-by-County Cost Matrix
          </h1>
          <p className="text-slate-300 max-w-3xl text-lg leading-relaxed mb-7">
            Compare entry cost, 5-year ownership cost, BER heating impact, and grant stack
            differences before committing to a new build or period property.
          </p>

          <div className="rounded-[2rem] border border-white/10 bg-white/5 backdrop-blur p-5 md:p-7">
            <div className="grid md:grid-cols-12 gap-5 items-end">
              <div className="md:col-span-8">
                <label className="text-[10px] uppercase tracking-wider font-black text-slate-300 mb-2 block">
                  Select County
                </label>
                <select
                  value={selectedCounty}
                  onChange={(event) => setSelectedCounty(event.target.value)}
                  className="w-full rounded-xl bg-white text-slate-900 px-4 py-3 font-semibold outline-none focus:ring-4 focus:ring-emerald-200"
                >
                  {NEW_VS_OLD_COUNTY_SEEDS.map((item) => (
                    <option key={item.county} value={item.county}>
                      {toTitleCase(item.county)} ({toTitleCase(item.town)})
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-4">
                <Link
                  to={selectedPath}
                  className="no-underline inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 px-4 py-3 text-sm font-black text-white hover:bg-emerald-600"
                >
                  Open Comparison <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <span className="text-xs font-black uppercase tracking-widest text-slate-400">Share:</span>
            <FacebookShareButton url={shareUrl} hashtag="#RentSmartIE">
              <FacebookIcon size={30} round />
            </FacebookShareButton>
            <TwitterShareButton url={shareUrl} title={shareText}>
              <TwitterIcon size={30} round />
            </TwitterShareButton>
            <WhatsappShareButton url={shareUrl} title={shareText}>
              <WhatsappIcon size={30} round />
            </WhatsappShareButton>
            <LinkedinShareButton url={shareUrl} title="New Build vs Old Build Ireland" summary={shareText}>
              <LinkedinIcon size={30} round />
            </LinkedinShareButton>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-10">
        <section className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <Wallet className="h-5 w-5 text-emerald-600 mb-2" />
            <h2 className="text-sm font-black text-slate-900 mb-1">5-Year Total Cost</h2>
            <p className="text-sm text-slate-600">
              Compare entry, mortgage, heating, maintenance, and retrofit drag over five years.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <Calculator className="h-5 w-5 text-sky-600 mb-2" />
            <h2 className="text-sm font-black text-slate-900 mb-1">Grant Stack Analysis</h2>
            <p className="text-sm text-slate-600">
              HTB + First Home Scheme vs SEAI + Croi Conaithe incentives in one decision view.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <Sparkles className="h-5 w-5 text-amber-600 mb-2" />
            <h2 className="text-sm font-black text-slate-900 mb-1">BER Effect</h2>
            <p className="text-sm text-slate-600">
              Quantifies how A-rated heating bills can offset higher entry prices over time.
            </p>
          </div>
        </section>

        <section className="rounded-[2rem] border border-slate-200 bg-white p-6 mb-8">
          <div className="relative max-w-xl mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search county or town (e.g. meath, navan, dublin)"
              className="w-full rounded-xl border border-slate-200 bg-slate-50 text-slate-900 pl-12 pr-4 py-3 outline-none focus:ring-4 focus:ring-cyan-100"
            />
          </div>

          <h2 className="text-2xl font-black text-slate-900 mb-4">Popular Comparisons</h2>
          {filtered.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center text-slate-500">
              No counties match your search.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((item) => {
                const detailSlug = toTownComparisonSlug(item.town);
                return (
                  <Link
                    key={detailSlug}
                    to={`/new-vs-old/${detailSlug}`}
                    className="no-underline rounded-2xl border border-slate-200 bg-white p-5 hover:-translate-y-1 hover:shadow-lg transition-all"
                  >
                    <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">
                      {toCountyComparisonSlug(item.county)}
                    </p>
                    <p className="text-xl font-black text-slate-900 mb-1">{toTitleCase(item.county)}</p>
                    <p className="text-sm font-semibold text-slate-600 mb-3">
                      Buying a new house vs period property in {toTitleCase(item.town)}
                    </p>
                    <p className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-wider text-emerald-700">
                      Open Audit <ArrowRight className="h-3 w-3" />
                    </p>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5 mb-8">
          <p className="text-xs text-amber-900 font-semibold leading-relaxed">
            These pages are estimate-led financial comparisons for planning. Validate grant eligibility,
            mortgage terms, BER assumptions, and renovation scope before making a purchase decision.
          </p>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-emerald-50 p-6">
          <h2 className="text-xl font-black text-slate-900 mb-2 flex items-center gap-2">
            <Share2 className="h-5 w-5 text-emerald-600" /> Share RentSmart.ie
          </h2>
          <p className="text-sm text-slate-600 font-medium mb-4 leading-relaxed">
            Share this New vs Old comparison matrix with first-time buyers researching their next move.
          </p>
          <div className="flex items-center gap-2">
            <FacebookShareButton url={shareUrl} hashtag="#RentSmartIE">
              <FacebookIcon size={32} round />
            </FacebookShareButton>
            <TwitterShareButton url={shareUrl} title={shareText}>
              <TwitterIcon size={32} round />
            </TwitterShareButton>
            <WhatsappShareButton url={shareUrl} title={shareText}>
              <WhatsappIcon size={32} round />
            </WhatsappShareButton>
            <LinkedinShareButton url={shareUrl} title="New Build vs Old Build Ireland" summary={shareText}>
              <LinkedinIcon size={32} round />
            </LinkedinShareButton>
          </div>
        </section>
      </main>
    </div>
  );
};

export default NewVsOldDiscoverPage;
